'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';
import { marketDataService } from '@/services/market-data.service';

interface LiveSignal {
  pair: string;
  type: 'BUY' | 'SELL';
  price: number;
  change: number;
  confidence: number;
  timestamp: Date;
  isMarketOpen?: boolean;
  isCrypto?: boolean;
  assetType?: 'crypto' | 'forex' | 'stock' | 'commodity';
}

interface LiveSignalsTickerProps {
  className?: string;
}

// Enhanced symbol list with diverse asset classes
const DEFAULT_SYMBOLS = [
  // Cryptocurrencies
  'BTC/USD',   // Bitcoin
  'ETH/USD',   // Ethereum

  // Stocks
  'AAPL',      // Apple Inc.
  'MSFT',      // Microsoft
  'GOOGL',     // Alphabet/Google

  // Forex
  'EUR/USD',   // Euro/Dollar
  'GBP/USD',   // British Pound/Dollar

  // Commodities
  'XAU/USD',   // Gold
  'XAG/USD',
];

const LiveSignalsTicker = ({ className = '' }: LiveSignalsTickerProps) => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [signals, setSignals] = useState<LiveSignal[]>([]);
  const [previousSignals, setPreviousSignals] = useState<Map<string, number>>(new Map());
  const [priceAnimations, setPriceAnimations] = useState<Map<string, 'up' | 'down' | null>>(new Map());
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  useEffect(() => {
    if (!isHydrated) return;

    const fetchLiveSignals = async () => {
      try {
        if (isLoading) {
          setError(null);
        }

        const quotes = await marketDataService.getBatchQuotes(DEFAULT_SYMBOLS);

        const liveSignals: LiveSignal[] = quotes.map(quote => {
          // Improved asset type detection
          const symbol = quote.symbol.toUpperCase();
          let assetType: 'crypto' | 'forex' | 'stock' | 'commodity' = 'stock';
          let isCrypto = false;

          // Crypto detection
          if (symbol.includes('BTC') || symbol.includes('ETH') ||
              symbol.includes('XRP') || symbol.includes('ADA') ||
              symbol.includes('SOL') || symbol.includes('DOGE') ||
              symbol.includes('MATIC') || symbol.includes('LINK')) {
            assetType = 'crypto';
            isCrypto = true;
          }
          // Commodity detection (Gold, Silver, etc.)
          else if (symbol.includes('XAU') || symbol.includes('XAG') ||
                   symbol.includes('GOLD') || symbol.includes('SILVER')) {
            assetType = 'commodity';
          }
          // Forex detection (currency pairs)
          else if (symbol.includes('EUR') || symbol.includes('GBP') ||
                   symbol.includes('JPY') || symbol.includes('AUD') ||
                   symbol.includes('CAD') || symbol.includes('CHF')) {
            assetType = 'forex';
          }
          // Everything else is a stock
          else {
            assetType = 'stock';
          }

          const isMarketOpen = quote.is_market_open ?? isCrypto;

          // Ensure timestamp is in milliseconds
          const timestampMs = quote.timestamp < 4102444800
            ? quote.timestamp * 1000
            : quote.timestamp;

          return {
            pair: quote.symbol,
            type: (quote.change || 0) >= 0 ? 'BUY' : 'SELL',
            price: quote.price,
            change: quote.percent_change || 0,
            confidence: Math.min(95, Math.max(75, 85 + Math.random() * 10)),
            timestamp: new Date(timestampMs),
            isMarketOpen,
            isCrypto,
            assetType,
          };
        });

        // Track price changes for animations
        const newAnimations = new Map<string, 'up' | 'down' | null>();
        liveSignals.forEach(signal => {
          const prevPrice = previousSignals.get(signal.pair);
          if (prevPrice !== undefined && prevPrice !== signal.price) {
            newAnimations.set(signal.pair, signal.price > prevPrice ? 'up' : 'down');
          }
        });

        setPriceAnimations(newAnimations);

        // Update previous prices
        const newPreviousPrices = new Map<string, number>();
        liveSignals.forEach(signal => {
          newPreviousPrices.set(signal.pair, signal.price);
        });
        setPreviousSignals(newPreviousPrices);

        setSignals(liveSignals);

        // Clear animations after they complete
        setTimeout(() => {
          setPriceAnimations(new Map());
        }, 800);

      } catch (err) {
        console.error('Error fetching live signals:', err);
        setError('Unable to load live market data. Please check your API configuration.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchLiveSignals();
    const interval = setInterval(fetchLiveSignals, 30000);

    return () => clearInterval(interval);
  }, [isHydrated]);

  useEffect(() => {
    if (!isHydrated || signals.length === 0) return;

    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % signals.length);
    }, 4000);

    return () => clearInterval(interval);
  }, [isHydrated, signals.length]);

  if (!isHydrated || isLoading) {
    return (
      <section className={`w-full bg-card border-y border-border py-6 ${className}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-3 h-3 bg-success rounded-full animate-pulse" />
              <span className="text-sm font-medium text-foreground">Loading Live Signals...</span>
            </div>
          </div>
        </div>
      </section>
    );
  }

  if (error || signals.length === 0) {
    return (
      <section className={`w-full bg-card border-y border-border py-6 ${className}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-3 h-3 bg-error rounded-full" />
              <span className="text-sm font-medium text-error">
                {error || 'Failed to load live market data'}
              </span>
            </div>
          </div>
        </div>
      </section>
    );
  }

  const currentSignal = signals[currentIndex];
  const timeAgo = Math.floor((Date.now() - currentSignal.timestamp.getTime()) / 1000);

  // Convert seconds to hours and minutes
  const hours = Math.floor(timeAgo / 3600);
  const minutes = Math.floor((timeAgo % 3600) / 60);

  let timeDisplay = '';
  if (hours > 0) {
    timeDisplay = `${hours} hr${hours !== 1 ? 's' : ''}`;
    if (minutes > 0) {
      timeDisplay += ` ${minutes} min${minutes !== 1 ? 's' : ''}`;
    }
  } else {
    timeDisplay = `${minutes} min${minutes !== 1 ? 's' : ''}`;
  }

  const showMarketClosed = !currentSignal.isMarketOpen && !currentSignal.isCrypto;
  const priceAnimation = priceAnimations.get(currentSignal.pair);

  // Asset type badge configuration
  const getAssetBadge = (assetType: string) => {
    switch(assetType) {
      case 'crypto':
        return { text: '24/7', color: 'bg-primary/20 text-primary' };
      case 'stock':
        return { text: 'STOCK', color: 'bg-blue-500/20 text-blue-500' };
      case 'commodity':
        return { text: 'COMMODITY', color: 'bg-yellow-500/20 text-yellow-500' };
      case 'forex':
        return { text: 'FOREX', color: 'bg-green-500/20 text-green-500' };
      default:
        return { text: 'MARKET', color: 'bg-gray-500/20 text-gray-500' };
    }
  };

  const assetBadge = getAssetBadge(currentSignal.assetType || 'stock');

  return (
    <section className={`w-full bg-card border-y border-border py-6 transition-all duration-300 ${className}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-4 bg-primary/10 border border-primary/20 rounded-lg p-3 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Icon name="InformationCircleIcon" size={20} className="text-primary" variant="solid" />
            <p className="text-sm text-foreground">
              <span className="font-semibold">Download Our App:</span> Get real-time trading signals and alerts on your mobile device
            </p>
          </div>
          <Link
            href="/app-download"
            className="px-4 py-1.5 bg-primary text-primary-foreground rounded-md text-sm font-medium hover:bg-primary/90 transition-colors whitespace-nowrap"
          >
            Download App
          </Link>
        </div>

        <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center space-x-3">
            <div className={`w-3 h-3 rounded-full ${showMarketClosed ? 'bg-warning' : 'bg-success animate-dot-pulse'}`} />
            <span className="text-sm font-medium text-foreground">
              {showMarketClosed ? 'Market Closed' : 'Live Trading Signals'}
            </span>
          </div>

          {showMarketClosed ? (
            <div className="flex items-center space-x-2 text-warning animate-slide-up-fade">
              <Icon name="ClockIcon" size={16} variant="solid" />
              <span className="text-sm font-medium">
                {currentSignal.pair} market is currently closed
              </span>
            </div>
          ) : (
            <div className={`flex items-center space-x-6 overflow-x-auto ${priceAnimation ? `animate-flash-${priceAnimation === 'up' ? 'green' : 'red'}` : ''}`}>
              <div className="flex items-center space-x-2 animate-slide-up-fade">
                <span className="text-sm font-semibold text-foreground">{currentSignal.pair}</span>
                <span className={`px-1.5 py-0.5 text-xs font-semibold rounded ${assetBadge.color} ${currentSignal.isCrypto ? 'animate-pulse-glow' : ''}`}>
                  {assetBadge.text}
                </span>
                <span
                  className={`px-2 py-1 text-xs font-semibold rounded transition-all duration-300 ${
                    currentSignal.type === 'BUY' ? 'bg-success/20 text-success' : 'bg-error/20 text-error'
                  }`}
                >
                  {currentSignal.type}
                </span>
              </div>

              <div className={`text-sm font-medium text-foreground price-transition ${priceAnimation ? `animate-price-${priceAnimation}` : ''}`}>
                ${currentSignal.price.toFixed(2)}
                {priceAnimation && (
                  <span className={`ml-1 inline-block ${priceAnimation === 'up' ? 'text-success' : 'text-error'}`}>
                    {priceAnimation === 'up' ? '↑' : '↓'}
                  </span>
                )}
              </div>

              <div
                className={`flex items-center space-x-1 transition-all duration-300 ${
                  currentSignal.change > 0 ? 'text-success' : 'text-error'
                }`}
              >
                <Icon
                  name={currentSignal.change > 0 ? 'ArrowUpIcon' : 'ArrowDownIcon'}
                  size={16}
                  variant="solid"
                  className="transition-transform duration-300"
                />
                <span className="text-sm font-medium">{Math.abs(currentSignal.change).toFixed(2)}%</span>
              </div>

              <div className="flex items-center space-x-1">
                <Icon name="SparklesIcon" size={16} className="text-primary" variant="solid" />
                <span className="text-sm text-muted-foreground">{currentSignal.confidence.toFixed(0)}%</span>
              </div>

              <span className="text-xs text-muted-foreground">{timeDisplay} ago</span>
            </div>
          )}

          <div className="flex items-center space-x-2">
            {signals.map((_, index) => (
              <div
                key={index}
                className={`h-2 rounded-full transition-all duration-300 ${
                  index === currentIndex ? 'bg-primary w-6' : 'bg-border w-2'
                }`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default LiveSignalsTicker;
